<template>
  <div class="preco-or-stock-prices">
    <nuxt-link
      class="preco-or-stock-prices-choice mb-0-6"
      :class="{ 'nuxt-link-active': productInPreco.current }"
      :to="productInPreco.to"
    >
      <div class="d-flex align-center">
        <div class="circle mr-1">
          <icon name="check" />
        </div>
        <div>
          <p class="text-secondary mb-0">{{ $t('purchase.price.preco') }}</p>
        </div>
      </div>

      <div
        v-if="loading"
        class="preco-or-stock-prices-choice__bottom d-flex justify-space-between align-center"
      >
        <span class="skeleton" style="width: 120px; height: 14px"></span>
        <span class="skeleton" style="width: 40px; height: 40px"></span>
      </div>
      <div
        v-else-if="productInPreco.shopifyProduct"
        class="preco-or-stock-prices-choice__bottom d-flex justify-space-between align-center"
      >
        <p class="text-small lh-1 mb-0">
          {{ $t('purchase.delivery') }}
          {{
            getDeliveryText(
              productInPreco.document,
              productInPreco.shopifyProduct
            )
          }}
        </p>

        <price :price="productInPreco.price" class="text-color-secondary" />
      </div>

      <slot v-if="productInPreco.current" />
    </nuxt-link>

    <nuxt-link
      class="preco-or-stock-prices-choice"
      :class="{ 'nuxt-link-active': productInStock.current }"
      :to="productInStock.to"
    >
      <div class="d-flex align-center">
        <div class="circle mr-1"><icon name="check" /></div>
        <div>
          <p class="text-secondary mb-0">{{ $t('purchase.price.stock') }}</p>
        </div>
      </div>

      <div
        v-if="loading"
        class="preco-or-stock-prices-choice__bottom d-flex justify-space-between align-center"
      >
        <span class="skeleton" style="width: 120px; height: 14px"></span>
        <span class="skeleton" style="width: 40px; height: 40px"></span>
      </div>
      <div
        v-else-if="productInPreco.shopifyProduct"
        class="preco-or-stock-prices-choice__bottom d-flex justify-space-between align-center"
      >
        <p class="text-small lh-1 mb-0">
          {{ $t('purchase.delivery') }}
          {{
            getDeliveryText(
              productInStock.document,
              productInStock.shopifyProduct
            )
          }}
        </p>

        <price :price="productInStock.price" class="text-color-secondary" />
      </div>

      <slot v-if="productInStock.current" />
    </nuxt-link>

    <p
      ref="tooltip"
      class="purchase-underline text-small text-center text-secondary mt-0-6 mb-0 tooltip"
    >
      <span>{{ $t('purchase.stock_preco_why') }}</span>
    </p>
  </div>
</template>

<script>
import { toHTML } from '@portabletext/to-html'

import Icon from '@/components/icons/Icon'
import Price from '@/components/Price/Price'

import DeliveryMixin from '@/mixins/deliveryText'

import { STATUS } from '@/const'
import services from '@/services'

export default {
  name: 'PrecoOrStockPrices',
  components: {
    Icon,
    Price,
  },
  mixins: [DeliveryMixin],
  props: {
    productInPreco: {
      type: Object,
      required: true,
    },
    productInStock: {
      type: Object,
      required: true,
    },
    precoOrStockShopifyProduct: {
      type: Object,
      default: () => ({}),
    },
    loading: {
      type: Boolean,
      default: false,
    },
    selectedColor: {
      type: String,
      default: null,
    },
    price: {
      type: Number,
      default: 0,
    },
    compareAtPrice: {
      type: Number,
      default: 0,
    },
  },
  mounted() {
    this.initTooltip()
  },
  methods: {
    getDeliveryText(productDocument, shopifyProduct) {
      let r = { ref: 'purchase.noDays', options: {} }

      if (
        productDocument.status === STATUS.PRECOMMANDE &&
        shopifyProduct.metafield
      ) {
        const d = new Date(shopifyProduct.metafield)
        if (!isNaN(d.getTime())) {
          r = this.getDeliveryFromDate(d)
          r.ref = 'purchase.between_small_punctuation'
        }
      }

      if (productDocument.status === STATUS.STOCK) {
        r = { ref: 'purchase.inAWeek', options: {} }
      }

      return this.$t(r.ref, r.options)
    },
    async initTooltip() {
      if (this.$refs.tooltip) {
        const tippy = await import('tippy.js' /* webpackChunkName: 'tippy' */)
        await import(
          '@/scss/09-libs/_06-tippy.scss' /* webpackChunkName: 'tippy_css' */
        )
        await import(
          'tippy.js/animations/scale.css' /* webpackChunkName: 'tippy_animation_scale_css' */
        )

        tippy.default(this.$refs.tooltip, {
          theme: 'dark',
          placement: 'top',
          animation: 'scale',
          content: this.$t('loading'),
          allowHTML: true,
          arrow: true,
          onShow: (instance) => {
            services.sanityService
              .getBiblio({
                ref: process.env.STOCK_PRECO_BIBLIO_UID,
                lang: this.$i18n.locale.slice(0, 2),
              })

              .then((biblio) => {
                if (biblio) {
                  instance.setContent(toHTML(biblio.explication))
                } else {
                  instance.setContent(this.$t('request.no-result'))
                }
              })
              .catch((err) => {
                instance.setContent(this.$t('request.failed'))
                this.$logError.captureException(err)
              })
          },
        })
      }
    },
  },
}
</script>

<style lang="scss">
.preco-or-stock-prices {
  .tooltip {
    letter-spacing: 0.28px;
  }

  .purchase__market {
    position: absolute;
    top: calc(var(--spacing) * 0.6);
    right: calc(var(--spacing) * 0.6);
    z-index: 10;
  }
}

.preco-or-stock-prices-choice {
  position: relative;
  display: block;
  padding: calc(var(--spacing) * 0.6) calc(var(--spacing) * 1);
  border: 1px solid RGB(0 46 93 / 0.2);
  border-radius: 2px;

  &::after {
    content: '';
    position: absolute;
    inset: rem(-1px);
    border: 1px solid var(--secondary-color);
    border-radius: 2px;
    opacity: 0;
    transition: all 0.32s var(--asphalte-animation-function);
  }

  @include on-hover-and-focus {
    &::after {
      opacity: 1;
    }
  }

  .circle {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    width: rem(20px);
    height: rem(20px);
    border: 1px solid var(--secondary-color-3);
    border-radius: 50%;
    background-color: white;

    &::before {
      content: '';
      position: absolute;
      top: 50%;
      left: 50%;
      width: rem(16px);
      height: rem(16px);
      background-color: var(--secondary-color);
      border-radius: 50%;
      transform: translate(-50%, -50%);
      opacity: 0;
      z-index: 1;
    }

    .icon {
      position: relative;
      width: rem(16px);
      height: rem(16px);
      color: var(--tertiary-color);
      opacity: 0;
      z-index: 2;
    }
  }

  .price__price {
    font-size: rem(24px);
    line-height: rem(40px);
  }

  &.nuxt-link-active {
    &::after {
      opacity: 1;
      border-width: 2px;
    }

    .circle {
      border-color: var(--secondary-color);

      &::before,
      .icon {
        opacity: 1;
      }
    }
  }
}

.preco-or-stock-prices-choice__bottom {
  padding-left: calc(#{rem(20px)} + var(--spacing));
}
</style>
